import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react'
import {
  Divider,
  TextField,
  Typography,
  Box,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material'
import { ListShowMoreIcon } from '../../../Svgs'
import { BoundingInfo, Mesh } from '@babylonjs/core'
import {
  computeBoundingBox,
  isDoorMesh,
  isSelectionIgnored,
  isWallMesh,
  isWindowMesh,
} from '../../../../visualization/utils'
import { SceneManager } from '../../../../visualization/sceneManager'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../../../store'
import { ConstructionTab } from '../../../../reducers/construction'
import { validate as isValidUUID } from 'uuid'
import {
  useEditWallTool,
  WallProperty,
} from '../../../../visualization/tools/useEditWallTool'
import {
  useEditWindowTool,
  WindowProperty,
} from '../../../../visualization/tools/useEditWindowTool'
import {
  DoorProperty,
  useEditDoorTool,
} from '../../../../visualization/tools/useEditDoorTool'

const numberRegex = /^\d+[.]?\d*$/

type PropertyTextFieldProps = {
  propertyName: string
  value: number
  isDisabled: boolean
  onChange?: (value: number) => void
  onConfirm?: () => void
  onDismiss?: () => void
}

const PropertyTextField = ({
  propertyName,
  value,
  isDisabled,
  onChange,
  onConfirm,
  onDismiss,
}: PropertyTextFieldProps) => {
  const [localValue, setLocalValue] = useState<string>(value.toString())

  useEffect(() => {
    setLocalValue(value.toString())
  }, [value])

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const originalValue = event.target.value
    const valueToTest = originalValue || '0'
    const parsedValue = parseFloat(valueToTest)

    if (!isNaN(parsedValue) && numberRegex.test(valueToTest)) {
      setLocalValue(originalValue)
      onChange?.(parsedValue)
    }
  }

  const handleTextFieldKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onConfirm?.()
    } else if (event.key === 'Escape') {
      onDismiss?.()
    } else {
      return
    }

    setLocalValue(value.toString())
  }

  const handleBlur = () => {
    onConfirm?.()
    setLocalValue(value.toString())
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
      }}
    >
      <Typography
        sx={{
          color: '#737373',
          fontFamily: 'Inter',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
          width: '40%',
          textAlign: 'left',
        }}
      >
        {propertyName}
      </Typography>

      <TextField
        size='small'
        autoComplete='off'
        value={localValue}
        disabled={isDisabled}
        onChange={handleTextFieldChange}
        onKeyUp={handleTextFieldKeyUp}
        onBlur={handleBlur}
        sx={{
          background: '#F5F5F5',
          borderRadius: '4px',
          backdropFilter: 'blur(2px)',
          '& .MuiInputBase-input': {
            fontSize: '14px',
            color: '#4F4F4F',
            fontFamily: 'Inter',
            lineHeight: 'normal',
            fontStyle: 'normal',
            fontWeight: 400,
            p: '6px 8px',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: 'none',
            },
          },
        }}
      />
    </Box>
  )
}

const WallProperties = ({
  mesh,
  isDisabled,
}: {
  mesh: Mesh
  isDisabled: boolean
}) => {
  const { changeWallLength, changeWallThickness, saveChanges, resetChanges } =
    useEditWallTool(mesh, false)

  const [wallProps, setWallProps] = useState({
    length: 0,
    height: 0,
    thickness: 0,
  })

  useEffect(() => {
    const updateWallProps = () => {
      let boundingBox
      if (mesh.geometry) {
        boundingBox = mesh.getBoundingInfo().boundingBox
      } else {
        const bbox = computeBoundingBox(mesh.getChildMeshes())
        boundingBox = new BoundingInfo(bbox.min, bbox.max).boundingBox
      }

      const {
        x: halfLength,
        y: halfHeight,
        z: halfThickness,
      } = boundingBox.extendSize

      setWallProps({
        length: halfLength * 2,
        height: halfHeight * 2,
        thickness: halfThickness * 2,
      })
    }

    updateWallProps()

    const onAfterWorldMatrixUpdateObserver =
      mesh.onAfterWorldMatrixUpdateObservable.add(updateWallProps)

    return () => {
      onAfterWorldMatrixUpdateObserver.remove()
    }
  }, [mesh])

  return (
    <>
      <PropertyTextField
        propertyName='Length'
        value={wallProps.length}
        isDisabled={isDisabled}
        onChange={(value) => changeWallLength(value)}
        onConfirm={() => saveChanges(WallProperty.Length)}
        onDismiss={() => resetChanges(WallProperty.Length)}
      />
      <PropertyTextField
        propertyName='Height'
        value={wallProps.height}
        isDisabled
      />
      <PropertyTextField
        propertyName='Thickness'
        value={wallProps.thickness}
        isDisabled={isDisabled}
        onChange={(value) => changeWallThickness(value)}
        onConfirm={() => saveChanges(WallProperty.Thickness)}
        onDismiss={() => resetChanges(WallProperty.Thickness)}
      />
    </>
  )
}

const WindowProperties = ({
  mesh,
  isDisabled,
}: {
  mesh: Mesh
  isDisabled: boolean
}) => {
  const { changeWindowWidth, changeWindowHeight, saveChanges, resetChanges } =
    useEditWindowTool(mesh, false)

  const [windowProps, setWindowProps] = useState({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const updateWindowProps = () => {
      let boundingBox
      if (mesh.geometry) {
        boundingBox = mesh.getBoundingInfo().boundingBox
      } else {
        const bbox = computeBoundingBox(mesh.getChildMeshes(), true)
        boundingBox = new BoundingInfo(bbox.min, bbox.max).boundingBox
      }

      const halfWidth = boundingBox.extendSize.x * mesh.scaling.x
      const halfHeight = boundingBox.extendSize.y * mesh.scaling.y

      setWindowProps({
        width: halfWidth * 2,
        height: halfHeight * 2,
      })
    }

    updateWindowProps()

    const onAfterWorldMatrixUpdateObserver =
      mesh.onAfterWorldMatrixUpdateObservable.add(updateWindowProps)

    return () => {
      onAfterWorldMatrixUpdateObserver.remove()
    }
  }, [mesh])

  return (
    <>
      <PropertyTextField
        propertyName='Width'
        value={windowProps.width}
        isDisabled={isDisabled}
        onChange={(value) => changeWindowWidth(value)}
        onConfirm={() => saveChanges(WindowProperty.Width)}
        onDismiss={() => resetChanges(WindowProperty.Width)}
      />
      <PropertyTextField
        propertyName='Height'
        value={windowProps.height}
        isDisabled={isDisabled}
        onChange={(value) => changeWindowHeight(value)}
        onConfirm={() => saveChanges(WindowProperty.Height)}
        onDismiss={() => resetChanges(WindowProperty.Height)}
      />
    </>
  )
}

const DoorProperties = ({
  mesh,
  isDisabled,
}: {
  mesh: Mesh
  isDisabled: boolean
}) => {
  const { changeDoorWidth, changeDoorHeight, saveChanges, resetChanges } =
    useEditDoorTool(mesh, false)

  const [doorProps, setDoorProps] = useState({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const updateDoorProps = () => {
      let boundingBox
      if (mesh.geometry) {
        boundingBox = mesh.getBoundingInfo().boundingBox
      } else {
        const bbox = computeBoundingBox(mesh.getChildMeshes())
        boundingBox = new BoundingInfo(bbox.min, bbox.max).boundingBox
      }

      const { x: halfWidth, y: halfHeight } = boundingBox.extendSize

      setDoorProps({
        width: halfWidth * 2,
        height: halfHeight * 2,
      })
    }

    updateDoorProps()

    const onAfterWorldMatrixUpdateObserver =
      mesh.onAfterWorldMatrixUpdateObservable.add(updateDoorProps)

    return () => {
      onAfterWorldMatrixUpdateObserver.remove()
    }
  }, [mesh])

  return (
    <>
      <PropertyTextField
        propertyName='Width'
        value={doorProps.width}
        isDisabled={isDisabled}
        onChange={(value) => changeDoorWidth(value)}
        onConfirm={() => saveChanges(DoorProperty.Width)}
        onDismiss={() => resetChanges(DoorProperty.Width)}
      />
      <PropertyTextField
        propertyName='Height'
        value={doorProps.height}
        isDisabled={isDisabled}
        onChange={(value) => changeDoorHeight(value)}
        onConfirm={() => saveChanges(DoorProperty.Height)}
        onDismiss={() => resetChanges(DoorProperty.Height)}
      />
    </>
  )
}

const selectSelectedElementId = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (constructionTab, tool, elementsIds) => {
    const elementId = elementsIds[0]
    if (
      constructionTab !== ConstructionTab.Editor ||
      !elementId ||
      !isValidUUID(elementId) ||
      isSelectionIgnored(tool)
    ) {
      return null
    }

    return elementId
  },
)

const ElementProperties = ({ isDisabled }: { isDisabled: boolean }) => {
  const [isOpen, setIsOpen] = useState(true)

  const selectedElementId = useSelector(selectSelectedElementId)
  const selectedMesh = useMemo(() => {
    if (!selectedElementId) {
      return null
    }

    const scene = SceneManager.getInstance().getScene()
    const mesh = scene?.getMeshById(selectedElementId)
    const allowedMeshTypes = [isWallMesh, isWindowMesh, isDoorMesh]

    if (
      mesh &&
      mesh instanceof Mesh &&
      allowedMeshTypes.some((fn) => fn(mesh))
    ) {
      return mesh
    }

    return null
  }, [selectedElementId])

  if (!selectedMesh) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Modify Object
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto'>
        <Stack
          direction='column'
          sx={{
            padding: '8px 12px',
            gap: '4px',
          }}
        >
          {isWallMesh(selectedMesh) && (
            <WallProperties mesh={selectedMesh} isDisabled={isDisabled} />
          )}

          {isWindowMesh(selectedMesh) && (
            <WindowProperties mesh={selectedMesh} isDisabled={isDisabled} />
          )}

          {isDoorMesh(selectedMesh) && (
            <DoorProperties mesh={selectedMesh} isDisabled={isDisabled} />
          )}
        </Stack>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>
    </Box>
  )
}

export default ElementProperties
